import * as THREE from 'three';
import galaxyVertexShader from '../shaders/vertex.glsl';
import galaxyFragmentShader from '../shaders/fragment.glsl';

export default class Galaxy {
    constructor(scene) {
        this.scene = scene;

        this.parameters = {
            count: 16000,
            size: 0.02,
            radius: 4,
            branches: 5,
            randomness: 0.3,
            randomnessPower: 4,
            insideColor: '#ed7b4d',
            outsideColor: '#4657de',
            innerRadius: 3.0,
            outerRadius: 4.0,
            thickness:0.2
        }

        this.geometry = null;
        this.material = null;
        this.points = null;

        this.setGalaxy();
    }

    setGalaxy() {
        this.removeGalaxy();

        this.geometry = new THREE.BufferGeometry();

        const positions = new Float32Array(this.parameters.count * 3);
        const colors = new Float32Array(this.parameters.count * 3);
        const scales = new Float32Array(this.parameters.count);
        const randomness = new Float32Array(this.parameters.count * 3);

        const colorInside = new THREE.Color(this.parameters.insideColor);
        const colorOutside = new THREE.Color(this.parameters.outsideColor);

        // ✅ 关键：给 inner/outer 做兜底，避免 undefined -> NaN
        const outer = (this.parameters.outerRadius ?? this.parameters.radius ?? 4);
        const inner = (this.parameters.innerRadius ?? outer * 0.5);
        const thickness = (this.parameters.thickness ?? 0.05);
        const jitter = (this.parameters.randomness ?? 0.03);

        const safeInner = Math.max(0.0001, Math.min(inner, outer - 0.0001));
        const safeOuter = Math.max(safeInner + 0.0001, outer);

        for (let i = 0; i < this.parameters.count; ++i) {
            const i3 = i * 3;

            // ✅ 面积均匀：sqrt 取半径
            const t = Math.random();
            const r = Math.sqrt(safeInner * safeInner + t * (safeOuter * safeOuter - safeInner * safeInner));
            const a = Math.random() * Math.PI * 2;

            let x = Math.cos(a) * r;
            let z = Math.sin(a) * r;

            // 平面内轻微抖动（不要乘 r，不然会散）
            x += (Math.random() - 0.5) * jitter;
            z += (Math.random() - 0.5) * jitter;

            positions[i3] = x;
            positions[i3 + 1] = (Math.random() - 0.5) * thickness;
            positions[i3 + 2] = z;

            randomness[i3] = (Math.random() - 0.5) * jitter;
            randomness[i3 + 1] = (Math.random() - 0.5) * thickness;
            randomness[i3 + 2] = (Math.random() - 0.5) * jitter;

            // 颜色按半径渐变（内->外）
            const u = (r - safeInner) / (safeOuter - safeInner);
            const mixed = colorInside.clone().lerp(colorOutside, u);

            colors[i3] = mixed.r;
            colors[i3 + 1] = mixed.g;
            colors[i3 + 2] = mixed.b;

            scales[i] = Math.random();
        }

        this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        this.geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1));
        this.geometry.setAttribute('aRandomness', new THREE.BufferAttribute(randomness, 3));

        // ✅ 很重要：更新包围球，避免被错误裁剪
        this.geometry.computeBoundingSphere();

        this.setMaterial();
        this.setPoints();
    }


    removeGalaxy() {
        if (this.points !== null) {
            this.geometry.dispose();
            this.material.dispose();
            this.scene.remove(this.points);
        }
    }

    setMaterial() {
        this.material = new THREE.ShaderMaterial({
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            vertexColors: true,
            vertexShader: galaxyVertexShader,
            fragmentShader: galaxyFragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uSize: { value: 30 * this.scene.renderer.getPixelRatio() }
            }
        })
    }

    setPoints() {
        this.points = new THREE.Points(this.geometry, this.material);
        this.scene.add(this.points);
    }
}